import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ChevronDown,
  ChevronUp,
  MessageCircle,
  Mail,
  Copy,
  Minus,
  Plus,
  Trash2,
  PackageX,
  Leaf,
  RefreshCw,
  PhoneOff,
  Link2,
  Check,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const NO_SUPPLIER_LABEL = "Senza fornitore";

/**
 * Section: "Da riordinare" — shown on top of the Suppliers page.
 * Groups under-stock products by supplier and lets the user send a pre-filled
 * order via WhatsApp (or fallback: copy text / email).
 */
export default function ReorderSection() {
  const { team } = useAuth();
  const storeName = team?.store?.name || "Magazzino";

  const [groups, setGroups] = useState(null); // null = loading
  const [refreshing, setRefreshing] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  // editable quantities + dropped items per group, keyed by supplier_name
  // shape: { [supplier]: { qty: { [product_id]: number }, removed: Set<product_id> } }
  const [edits, setEdits] = useState({});
  const [expanded, setExpanded] = useState({}); // supplier_name -> bool

  const load = async () => {
    setRefreshing(true);
    try {
      const [data, sups] = await Promise.all([
        api.getReorderSuggestions(),
        api.listSuppliers().catch(() => []),
      ]);
      setGroups(data);
      setSuppliers(sups || []);
      // initialize edits with backend-suggested quantities, all expanded by default if only one group
      const next = {};
      const exp = {};
      for (const g of data) {
        next[g.supplier_name] = {
          qty: Object.fromEntries(g.items.map((it) => [it.product_id, it.suggested_qty])),
          removed: new Set(),
        };
        exp[g.supplier_name] = data.length === 1;
      }
      setEdits(next);
      setExpanded(exp);
    } catch (e) {
      toast.error("Impossibile caricare i suggerimenti", { description: e?.response?.data?.detail || e.message });
      setGroups([]);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    // auto-scroll to the reorder section if requested via hash
    if (typeof window !== "undefined" && window.location.hash === "#riordino") {
      setTimeout(() => {
        document.getElementById("riordino")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
    }
  }, []);

  const totalToReorder = useMemo(() => {
    if (!groups) return 0;
    return groups.reduce((sum, g) => {
      const ed = edits[g.supplier_name];
      if (!ed) return sum + g.items.length;
      return sum + g.items.filter((it) => !ed.removed.has(it.product_id)).length;
    }, 0);
  }, [groups, edits]);

  if (groups === null) {
    return (
      <div data-testid="reorder-loading" className="mag-card p-6 text-sm text-[#8E918C]">
        Caricamento suggerimenti di riordino…
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <div
        id="riordino"
        data-testid="reorder-empty"
        className="mag-card p-6 flex items-start gap-3 border-[#C5E1D1] bg-[#E8F3EC]"
      >
        <div className="h-10 w-10 rounded-xl bg-white text-[#2B5A3F] flex items-center justify-center flex-shrink-0">
          <Leaf size={18} />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-[#1A1C1A]" style={{ fontFamily: "Work Sans, sans-serif" }}>
            Tutto sotto controllo
          </h3>
          <p className="text-sm text-[#2B5A3F] mt-1">
            Nessun prodotto sotto la soglia di giacenza minima. Quando uno scenderà, lo vedrai qui pronto da riordinare.
          </p>
        </div>
      </div>
    );
  }

  return (
    <section id="riordino" data-testid="reorder-section" className="space-y-4">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#8E918C] mb-1">Suggerimenti</p>
          <h2 className="text-xl sm:text-2xl font-semibold tracking-tight" style={{ fontFamily: "Work Sans, sans-serif" }}>
            Da riordinare
          </h2>
          <p className="text-sm text-[#5C5F5A] mt-1">
            {totalToReorder} articol{totalToReorder === 1 ? "o" : "i"} sotto la giacenza minima, raggruppati per fornitore.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          disabled={refreshing}
          data-testid="reorder-refresh-btn"
          className="border-[#EAE7E1] bg-white"
        >
          <RefreshCw size={14} className={`mr-2 ${refreshing ? "animate-spin" : ""}`} />
          Aggiorna
        </Button>
      </div>

      <div className="space-y-3">
        {groups.map((g) => {
          const ed = edits[g.supplier_name] || { qty: {}, removed: new Set() };
          const activeItems = g.items.filter((it) => !ed.removed.has(it.product_id));
          const isOpen = !!expanded[g.supplier_name];
          return (
            <ReorderGroup
              key={g.supplier_name}
              group={g}
              storeName={storeName}
              suppliers={suppliers}
              onReload={load}
              isOpen={isOpen}
              onToggle={() => setExpanded((s) => ({ ...s, [g.supplier_name]: !s[g.supplier_name] }))}
              activeItems={activeItems}
              qty={ed.qty}
              setQty={(productId, value) => {
                setEdits((s) => ({
                  ...s,
                  [g.supplier_name]: {
                    ...s[g.supplier_name],
                    qty: { ...s[g.supplier_name].qty, [productId]: value },
                  },
                }));
              }}
              onRemove={(productId) => {
                setEdits((s) => {
                  const cur = s[g.supplier_name];
                  const removed = new Set(cur.removed);
                  removed.add(productId);
                  return { ...s, [g.supplier_name]: { ...cur, removed } };
                });
              }}
            />
          );
        })}
      </div>
    </section>
  );
}

function ReorderGroup({ group, storeName, suppliers, onReload, isOpen, onToggle, activeItems, qty, setQty, onRemove }) {
  const hasContact = !!(group.whatsapp || group.phone || group.email);
  const isNoSupplierGroup = group.supplier_name === NO_SUPPLIER_LABEL;
  // Prodotti attribuiti "a lettura" (visualizzazione dal normalizer): supplier_id
  // sul prodotto è null, ma il riordino li ha mostrati qui. La UI segnala che
  // il link non è ancora scritto e offre un pulsante "Conferma collegamento".
  const autoItems = (group.items || []).filter((it) => it.link_status === "auto");
  const hasAutoItems = !isNoSupplierGroup && autoItems.length > 0 && !!group.supplier_id;

  // Assegnazione bulk sul gruppo "Senza fornitore"
  const [pickerOpen, setPickerOpen] = useState(false);
  const [picked, setPicked] = useState(new Set()); // product_ids selezionati
  const [targetSupId, setTargetSupId] = useState("");
  const [assignBusy, setAssignBusy] = useState(false);

  const togglePicked = (pid) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid);
      else next.add(pid);
      return next;
    });
  };

  const runAssign = async () => {
    if (!targetSupId || picked.size === 0) return;
    setAssignBusy(true);
    try {
      const res = await api.assignSupplier([...picked], targetSupId);
      toast.success("Prodotti collegati", {
        description: `${res.products_updated} prodotti assegnati a ${res.supplier?.name || "fornitore"}.`,
      });
      setPickerOpen(false);
      setPicked(new Set());
      setTargetSupId("");
      onReload && onReload();
    } catch (e) {
      toast.error("Errore", { description: e?.response?.data?.detail || e.message });
    } finally {
      setAssignBusy(false);
    }
  };

  const runConfirmAuto = async () => {
    if (!group.supplier_id || autoItems.length === 0) return;
    setAssignBusy(true);
    try {
      const res = await api.assignSupplier(
        autoItems.map((it) => it.product_id),
        group.supplier_id,
      );
      toast.success("Collegamento confermato", {
        description: `${res.products_updated} prodotti collegati a ${group.supplier_name}.`,
      });
      onReload && onReload();
    } catch (e) {
      toast.error("Errore", { description: e?.response?.data?.detail || e.message });
    } finally {
      setAssignBusy(false);
    }
  };

  const orderLines = activeItems.map((it) => {
    const q = qty[it.product_id] ?? it.suggested_qty;
    // Se il prodotto ha un codice articolo del fornitore, includilo nel messaggio
    // per rendere l'ordine più veloce da evadere dal listino cartaceo del fornitore.
    const code = it.supplier_code ? ` [${it.supplier_code}]` : "";
    return `- ${q}${it.unit} ${it.name}${code}`;
  });
  const dateStr = new Date().toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
  const orderText = `Ordine ${storeName} - ${dateStr}\n${orderLines.join("\n")}\n\nGrazie!`;

  const sendWhatsApp = () => {
    if (activeItems.length === 0) {
      toast.error("Nessun articolo da ordinare");
      return;
    }
    if (!group.whatsapp) return;
    const url = `https://wa.me/${group.whatsapp}?text=${encodeURIComponent(orderText)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const sendEmail = () => {
    if (activeItems.length === 0) {
      toast.error("Nessun articolo da ordinare");
      return;
    }
    if (!group.email) return;
    const subject = encodeURIComponent(`Ordine ${storeName} - ${dateStr}`);
    const body = encodeURIComponent(orderText);
    window.location.href = `mailto:${group.email}?subject=${subject}&body=${body}`;
  };

  const copyText = async () => {
    if (activeItems.length === 0) {
      toast.error("Nessun articolo da ordinare");
      return;
    }
    try {
      await navigator.clipboard.writeText(orderText);
      toast.success("Testo dell'ordine copiato negli appunti");
    } catch (_e) {
      toast.error("Impossibile copiare. Selezionalo manualmente.");
    }
  };

  return (
    <div
      data-testid={`reorder-group-${group.supplier_name}`}
      className="mag-card overflow-hidden"
    >
      <button
        type="button"
        onClick={onToggle}
        data-testid={`reorder-group-toggle-${group.supplier_name}`}
        className="w-full flex items-center justify-between gap-3 p-5 text-left hover:bg-[#F9F8F6] transition-colors"
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="h-10 w-10 rounded-xl bg-[#FDF6E3] text-[#8B6518] flex items-center justify-center flex-shrink-0">
            <PackageX size={18} />
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-sm truncate">{group.supplier_name}</div>
            <div className="text-xs text-[#8E918C] mt-0.5 flex items-center gap-2 flex-wrap">
              <span>
                {activeItems.length} articol{activeItems.length === 1 ? "o" : "i"} da riordinare
              </span>
              {group.whatsapp && (
                <span className="inline-flex items-center gap-1 text-[#2B5A3F]">
                  · <MessageCircle size={10} /> WhatsApp
                </span>
              )}
              {!hasContact && (
                <span className="inline-flex items-center gap-1 text-[#8E3B2A]">
                  · <PhoneOff size={10} /> Nessun contatto
                </span>
              )}
            </div>
          </div>
        </div>
        {isOpen ? <ChevronUp size={18} className="text-[#8E918C] flex-shrink-0" /> : <ChevronDown size={18} className="text-[#8E918C] flex-shrink-0" />}
      </button>

      {isOpen && (
        <div className="border-t border-[#EAE7E1] p-5 space-y-3 bg-[#FAF9F6]">
          {activeItems.length === 0 ? (
            <div className="text-sm text-[#8E918C] text-center py-4">
              Tutti gli articoli di questo fornitore sono stati rimossi dall&apos;ordine.
            </div>
          ) : (
            <div className="space-y-2">
              {activeItems.map((it) => {
                const q = qty[it.product_id] ?? it.suggested_qty;
                return (
                  <div
                    key={it.product_id}
                    data-testid={`reorder-item-${it.product_id}`}
                    className="p-3 rounded-lg bg-white border border-[#EAE7E1] space-y-2"
                  >
                    {/* Riga 1: nome prodotto a piena larghezza, wrap consentito
                        fino a 2 righe, puntini solo dopo la seconda riga. */}
                    <div
                      className="text-sm font-medium break-words [overflow-wrap:anywhere] leading-snug"
                      style={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {it.name}
                    </div>
                    {/* Riga 2: badge + attributi (fresco, attribuito), inline. */}
                    {(it.is_fresh || it.link_status === "auto") && (
                      <div className="flex flex-wrap items-center gap-1.5">
                        {it.is_fresh && (
                          <span
                            title="Prodotto fresco: tetto anti-spreco applicato"
                            className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-[#2B5A3F] bg-[#E8F3EC] border border-[#C5E1D1] px-1.5 py-0.5 rounded"
                          >
                            <Leaf size={9} />
                            Fresco
                          </span>
                        )}
                        {it.link_status === "auto" && (
                          <span
                            data-testid={`reorder-item-auto-${it.product_id}`}
                            title="Attribuito automaticamente dalla ragione sociale letta sul documento"
                            className="text-[10px] text-[#8B6518] italic"
                          >
                            attribuito automaticamente
                          </span>
                        )}
                      </div>
                    )}
                    {/* Riga 3: giacenza · soglia, una riga sola, no wrap tra parole */}
                    <div className="text-xs text-[#8E918C] whitespace-nowrap overflow-hidden text-ellipsis">
                      Giacenza: {it.unit === "kg" ? `${Number(it.stock).toFixed(3).replace(".", ",")} kg` : `${it.stock} pz`} · Soglia: {it.unit === "kg" ? `${Number(it.min_stock).toFixed(3).replace(".", ",")} kg` : `${it.min_stock} pz`}
                    </div>
                    {/* Riga 4: controlli quantità + cestino, allineati a destra */}
                    <div className="flex items-center justify-end gap-2">
                      <QtyInput
                        value={q}
                        unit={it.unit}
                        onChange={(v) => setQty(it.product_id, v)}
                        testid={`reorder-qty-${it.product_id}`}
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-9 w-9 text-[#C65D47] hover:bg-[#FCEAE8] flex-shrink-0"
                        onClick={() => onRemove(it.product_id)}
                        data-testid={`reorder-remove-${it.product_id}`}
                        title="Rimuovi dall'ordine"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Preview text */}
          {activeItems.length > 0 && (
            <details className="text-xs text-[#5C5F5A]">
              <summary className="cursor-pointer select-none text-[#2E4A3B] font-medium">
                Anteprima testo ordine
              </summary>
              <pre
                data-testid={`reorder-preview-${group.supplier_name}`}
                className="mt-2 p-3 rounded-md bg-[#F4F1ED] border border-[#EAE7E1] whitespace-pre-wrap font-mono text-[11px] leading-relaxed"
              >
                {orderText}
              </pre>
            </details>
          )}

          {/* Conferma collegamento per prodotti attribuiti a lettura */}
          {hasAutoItems && (
            <div
              data-testid={`reorder-confirm-auto-${group.supplier_name}`}
              className="flex flex-wrap items-center gap-2 p-3 rounded-md bg-[#FDF6E3] border border-[#F2DEB0]"
            >
              <Link2 size={14} className="text-[#8B6518]" />
              <div className="text-xs text-[#5C5F5A] flex-1 min-w-0">
                {autoItems.length} prodott{autoItems.length === 1 ? "o" : "i"} in questo gruppo {autoItems.length === 1 ? "è" : "sono"} attribuit{autoItems.length === 1 ? "o" : "i"} a lettura. Conferma il collegamento per scrivere la scheda in modo stabile.
              </div>
              <Button
                type="button"
                size="sm"
                onClick={runConfirmAuto}
                disabled={assignBusy}
                data-testid={`reorder-confirm-auto-btn-${group.supplier_name}`}
                className="bg-[#8B6518] hover:bg-[#6E5113] text-white h-8 text-xs"
              >
                <Check size={12} className="mr-1" />
                Conferma collegamento
              </Button>
            </div>
          )}

          {/* Assegnazione bulk sul gruppo "Senza fornitore" */}
          {isNoSupplierGroup && activeItems.length > 0 && (
            <div className="p-3 rounded-md bg-white border border-[#EAE7E1]">
              {!pickerOpen ? (
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="text-xs text-[#5C5F5A]">
                    Questi prodotti non hanno una scheda fornitore.
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setPickerOpen(true)}
                    data-testid="reorder-assign-open-btn"
                    className="border-[#2E4A3B] text-[#2E4A3B] hover:bg-[#E8F3EC] h-8 text-xs"
                  >
                    <Link2 size={12} className="mr-1" />
                    Assegna
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="text-xs font-semibold text-[#5C5F5A] uppercase tracking-wider">
                    Seleziona i prodotti e la scheda
                  </div>
                  <div className="max-h-48 overflow-y-auto space-y-1 border border-[#EAE7E1] rounded p-2 bg-[#FAF9F6]">
                    {activeItems.map((it) => (
                      <label
                        key={it.product_id}
                        data-testid={`reorder-assign-pick-${it.product_id}`}
                        className="flex items-start gap-2 text-sm py-1.5 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={picked.has(it.product_id)}
                          onChange={() => togglePicked(it.product_id)}
                          className="mt-0.5 flex-shrink-0"
                        />
                        <span className="min-w-0 flex-1 break-words [overflow-wrap:anywhere] leading-snug">
                          {it.name}
                        </span>
                      </label>
                    ))}
                  </div>
                  <Select value={targetSupId} onValueChange={setTargetSupId}>
                    <SelectTrigger data-testid="reorder-assign-supplier-select" className="h-9 text-sm">
                      <SelectValue placeholder="Scegli una scheda fornitore" />
                    </SelectTrigger>
                    <SelectContent>
                      {suppliers.length === 0 ? (
                        <div className="text-xs text-[#8E918C] p-2">
                          Nessuna scheda fornitore disponibile.
                        </div>
                      ) : (
                        suppliers.map((s) => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <div className="flex gap-2 justify-end">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => { setPickerOpen(false); setPicked(new Set()); setTargetSupId(""); }}
                      disabled={assignBusy}
                      className="border-[#EAE7E1] h-8 text-xs"
                    >
                      Annulla
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      onClick={runAssign}
                      disabled={assignBusy || picked.size === 0 || !targetSupId}
                      data-testid="reorder-assign-confirm-btn"
                      className="bg-[#2E4A3B] hover:bg-[#21382C] text-white h-8 text-xs"
                    >
                      {assignBusy ? "Assegnazione…" : `Assegna ${picked.size} prodotti`}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap gap-2 pt-2 border-t border-[#EAE7E1]">
            {group.whatsapp ? (
              <Button
                onClick={sendWhatsApp}
                data-testid={`reorder-send-whatsapp-${group.supplier_name}`}
                disabled={activeItems.length === 0}
                className="bg-[#25D366] hover:bg-[#1FB85A] text-white"
              >
                <MessageCircle size={14} className="mr-2" />
                Invia ordine su WhatsApp
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={copyText}
                data-testid={`reorder-copy-${group.supplier_name}`}
                disabled={activeItems.length === 0}
                className="border-[#EAE7E1] bg-white"
              >
                <Copy size={14} className="mr-2" />
                Copia testo ordine
              </Button>
            )}
            {group.email && (
              <Button
                variant="outline"
                onClick={sendEmail}
                data-testid={`reorder-send-email-${group.supplier_name}`}
                disabled={activeItems.length === 0}
                className="border-[#EAE7E1] bg-white"
              >
                <Mail size={14} className="mr-2" />
                Invia via email
              </Button>
            )}
            {!hasContact && (
              <div className="text-xs text-[#8E3B2A] bg-[#FCEAE8] border border-[#F5C7BD] px-3 py-2 rounded-md flex items-center gap-2">
                <PhoneOff size={12} />
                Nessun recapito impostato per &quot;{group.supplier_name}&quot;. Aggiungi WhatsApp/email dal pulsante &quot;Modifica&quot; sulla card del fornitore.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function QtyInput({ value, unit, onChange, testid }) {
  // Use string state to allow intermediate editing (e.g. ".5")
  const [raw, setRaw] = useState(String(value ?? 0));
  useEffect(() => {
    setRaw(String(value ?? 0));
  }, [value]);

  const numeric = Number(raw);
  const isKg = unit === "kg";
  const step = isKg ? 0.5 : 1;

  const commit = (next) => {
    const n = Number(next);
    if (Number.isFinite(n) && n >= 0) {
      onChange(isKg ? Number(n.toFixed(2)) : Math.max(0, Math.round(n)));
    } else {
      setRaw(String(value ?? 0));
    }
  };

  const dec = () => {
    const next = Math.max(0, (Number.isFinite(numeric) ? numeric : 0) - step);
    onChange(isKg ? Number(next.toFixed(2)) : Math.round(next));
  };
  const inc = () => {
    const next = (Number.isFinite(numeric) ? numeric : 0) + step;
    onChange(isKg ? Number(next.toFixed(2)) : Math.round(next));
  };

  return (
    <div className="flex items-center gap-1 flex-shrink-0">
      <Button
        type="button"
        variant="outline"
        size="icon"
        onClick={dec}
        className="h-9 w-9 border-[#EAE7E1] bg-white"
        aria-label="Diminuisci"
      >
        <Minus size={12} />
      </Button>
      <div className="flex items-center bg-white border border-[#EAE7E1] rounded-md h-9 w-20 overflow-hidden">
        <Input
          data-testid={testid}
          type="text"
          inputMode={isKg ? "decimal" : "numeric"}
          value={raw}
          onChange={(e) => setRaw(e.target.value.replace(",", "."))}
          onBlur={() => commit(raw)}
          onKeyDown={(e) => { if (e.key === "Enter") commit(raw); }}
          className="border-0 h-full px-2 text-center text-sm focus-visible:ring-0 focus-visible:ring-offset-0"
        />
        <span className="text-[10px] text-[#8E918C] pr-2 select-none">{unit}</span>
      </div>
      <Button
        type="button"
        variant="outline"
        size="icon"
        onClick={inc}
        className="h-9 w-9 border-[#EAE7E1] bg-white"
        aria-label="Aumenta"
      >
        <Plus size={12} />
      </Button>
    </div>
  );
}
